import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Scatter,
  ComposedChart,
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faSpinner, faSync } from '@fortawesome/free-solid-svg-icons';
import { marketDataApi } from '../api/marketDataApi';

function smaSeries(closes, period) {
  const out = new Array(closes.length).fill(null);
  for (let i = period - 1; i < closes.length; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += closes[j];
    out[i] = sum / period;
  }
  return out;
}

function rsiSeries(closes, period = 14) {
  const out = new Array(closes.length).fill(null);
  if (closes.length < period + 1) return out;
  for (let i = period; i < closes.length; i++) {
    let gains = 0;
    let losses = 0;
    for (let j = i - period + 1; j <= i; j++) {
      const diff = closes[j] - closes[j - 1];
      if (diff >= 0) gains += diff;
      else losses -= diff;
    }
    const rs = losses === 0 ? 100 : gains / losses;
    out[i] = 100 - 100 / (1 + rs);
  }
  return out;
}

/** Session VWAP from OHLCV bars (resets each calendar day). */
function vwapSeries(bars) {
  const out = new Array(bars.length).fill(null);
  let dayKey = null;
  let cumPv = 0;
  let cumV = 0;
  for (let i = 0; i < bars.length; i++) {
    const b = bars[i];
    const d = new Date(b.t);
    const key = `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`;
    if (key !== dayKey) {
      dayKey = key;
      cumPv = 0;
      cumV = 0;
    }
    const tp = (Number(b.high) + Number(b.low) + Number(b.close)) / 3;
    const vol = Math.max(Number(b.volume) || 0, 1);
    cumPv += tp * vol;
    cumV += vol;
    out[i] = cumV > 0 ? cumPv / cumV : null;
  }
  return out;
}

function formatDay(ts) {
  try {
    return new Date(ts).toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

function formatTime(ts) {
  try {
    const s = new Date(ts).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${s} ET`;
  } catch {
    return ts;
  }
}

/** Pull numeric / string consts from the editor script so the chart matches edits. */
function parseScriptParams(script) {
  if (!script || typeof script !== 'string') return {};
  const out = {};
  const numRe =
    /(?:^|\n)\s*(?:const|let|var)\s+(fastPeriod|slowPeriod|fastLen|slowLen|period|oversold|overbought|coverBelow|lookback|trendBars|minExtensionPct|touchBandPct|zScoreEntry|zScoreExit|orMinutes)\s*=\s*([0-9.]+)/g;
  let m;
  while ((m = numRe.exec(script))) {
    out[m[1]] = Number(m[2]);
  }
  if (out.fastLen != null && out.fastPeriod == null) out.fastPeriod = out.fastLen;
  if (out.slowLen != null && out.slowPeriod == null) out.slowPeriod = out.slowLen;
  const sym = script.match(/(?:^|\n)\s*(?:const|let|var)\s+symbol\s*=\s*['"]([A-Z0-9.]+)['"]/);
  if (sym) out.symbol = sym[1];
  const symY = script.match(/(?:^|\n)\s*(?:const|let|var)\s+symbolY\s*=\s*['"]([A-Z0-9.]+)['"]/);
  if (symY) out.symbolY = symY[1];
  const symX = script.match(/(?:^|\n)\s*(?:const|let|var)\s+symbolX\s*=\s*['"]([A-Z0-9.]+)['"]/);
  if (symX) out.symbolX = symX[1];
  return out;
}

function resolveMode(templateId) {
  switch (templateId) {
    case 'rsi_mean_reversion':
      return 'rsi';
    case 'rsi_overbought_short':
      return 'rsi_short';
    case 'momentum_breakout':
      return 'breakout';
    case 'momentum_breakdown_short':
      return 'breakdown';
    case 'twap_slice':
      return 'twap';
    case 'or_fakeout':
      return 'or_fakeout';
    case 'vwap_day_trade':
    case 'vwap_rejection_short':
      return 'vwap';
    case 'sma_death_cross_short':
      return 'sma_short';
    case 'intraday_bear_momentum':
      return 'bear_momentum';
    case 'pairs_cointegration':
    case 'pairs_spread_pct':
      return 'pairs';
    case 'sma_crossover':
      return 'sma';
    case '':
    case null:
    case undefined:
      return 'sma';
    default:
      // Unknown / custom — still show SMA overlay as a useful default
      return 'sma';
  }
}

function modeLabel(mode, p) {
  switch (mode) {
    case 'sma':
      return `SMA ${p.fastPeriod}/${p.slowPeriod} · golden/death`;
    case 'sma_short':
      return `SMA ${p.fastPeriod}/${p.slowPeriod} · death short / golden cover`;
    case 'rsi':
      return `RSI(${p.rsiPeriod}) ${p.oversold}/${p.overbought}`;
    case 'rsi_short':
      return `RSI(${p.rsiPeriod}) short>${p.overbought} cover<${p.coverBelow}`;
    case 'breakout':
      return `${p.lookback}d high breakout`;
    case 'breakdown':
      return `${p.lookback}d low breakdown`;
    case 'or_fakeout':
      return `Opening range fakeout (${p.orMinutes || 15}m)`;
    case 'vwap':
      return 'Session VWAP pullback / rejection (5m)';
    case 'twap':
      return 'Price + live TWAP / order markers';
    case 'pairs':
      return 'Pairs · both legs';
    case 'bear_momentum':
      return 'EMA crossunder + VWAP filter (15m)';
    default:
      return mode;
  }
}

function mapApiBars(raw) {
  return (Array.isArray(raw) ? raw : [])
    .map((bar) => {
      const timestamp = bar.timestamp || bar.t || bar.time || bar.date;
      const close = Number(bar.close ?? bar.c);
      const high = Number(bar.high ?? bar.h ?? close);
      const low = Number(bar.low ?? bar.l ?? close);
      const volume = Number(bar.volume ?? bar.v ?? 0);
      return {
        t: timestamp,
        close: Number.isFinite(close) ? close : null,
        high: Number.isFinite(high) ? high : null,
        low: Number.isFinite(low) ? low : null,
        volume: Number.isFinite(volume) ? volume : 0,
      };
    })
    .filter((p) => p.t && Number.isFinite(p.close))
    .sort((a, b) => new Date(a.t) - new Date(b.t));
}

function eventIsBuy(ev) {
  const side = String(ev.side || '').toLowerCase();
  const kind = String(ev.kind || '');
  return (
    side === 'buy' ||
    kind.includes('golden') ||
    kind.includes('oversold') ||
    kind.includes('breakout') ||
    kind.includes('cover') ||
    kind.includes('vwap_entry') ||
    kind.includes('or_fakeout_long') ||
    kind.includes('pairs_tight') ||
    kind === 'twap_start' ||
    kind === 'order'
  );
}

function eventIsSell(ev) {
  const side = String(ev.side || '').toLowerCase();
  const kind = String(ev.kind || '');
  return (
    side === 'sell' ||
    kind.includes('death') ||
    kind.includes('overbought') ||
    kind.includes('breakdown') ||
    kind.includes('vwap_short') ||
    kind.includes('or_fakeout_short') ||
    kind.includes('pairs_wide') ||
    kind === 'exit'
  );
}

/** Price series with buy/sell marks for one symbol (events filtered by symbol when present). */
function buildLegChartData(bars, events, forSymbol, timeframe = '1Day') {
  if (!bars?.length) return [];
  const sym = String(forSymbol || '').toUpperCase();
  const rows = bars.map((b) => ({
    t: b.t,
    label: timeframe.includes('Day') ? formatDay(b.t) : formatTime(b.t),
    close: b.close,
    buyMark: null,
    sellMark: null,
  }));
  const maxDiffMs = String(timeframe).includes('Day') ? 36 * 60 * 60 * 1000 : 6 * 60 * 60 * 1000;
  for (const ev of events || []) {
    if (!ev?.timestamp && !ev?.t) continue;
    const evSym = String(ev.symbol || '').toUpperCase();
    if (evSym && sym && evSym !== sym) continue;
    const evT = new Date(ev.timestamp || ev.t).getTime();
    if (!Number.isFinite(evT)) continue;
    let best = -1;
    let bestDiff = Infinity;
    for (let i = 0; i < rows.length; i++) {
      const diff = Math.abs(new Date(rows[i].t).getTime() - evT);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = i;
      }
    }
    if (best < 0 || bestDiff > maxDiffMs) continue;
    if (eventIsBuy(ev)) rows[best].buyMark = ev.price != null ? Number(ev.price) : rows[best].close;
    if (eventIsSell(ev)) rows[best].sellMark = ev.price != null ? Number(ev.price) : rows[best].close;
  }
  return rows;
}

const EVENT_COLORS = {
  golden_cross: '#16a34a',
  death_cross: '#dc2626',
  oversold: '#16a34a',
  overbought: '#dc2626',
  cover: '#16a34a',
  breakout: '#2563eb',
  breakdown: '#dc2626',
  or_fakeout_long: '#16a34a',
  or_fakeout_short: '#dc2626',
  vwap_entry: '#16a34a',
  vwap_short: '#dc2626',
  pairs_wide: '#dc2626',
  pairs_tight: '#16a34a',
  pairs_exit: '#ea580c',
  order: '#7c3aed',
  exit: '#ea580c',
  twap_start: '#0891b2',
  signal: '#4f46e5',
};

/**
 * Price + indicator chart with historical signal markers and live algo events.
 * Updates markers/overlays from the selected strategy template (+ script consts).
 * Optional backtestRange + backtestEvents overlay the run window and emitSignal markers.
 * Pass symbols=[Y,X] (or IDE desk list) to chart multiple legs.
 */
const AlgoSignalChart = ({
  symbol,
  symbols = [],
  templateId = '',
  templateName = '',
  params = {},
  script = '',
  liveEvents = [],
  backtestRange = null,
  backtestEvents = [],
  height = 320,
}) => {
  const [barsBySymbol, setBarsBySymbol] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);

  const mode = useMemo(() => resolveMode(templateId), [templateId]);
  const inBacktest = Boolean(backtestRange?.start && backtestRange?.end);

  const mergedParams = useMemo(() => {
    const fromScript = parseScriptParams(script);
    return { ...params, ...fromScript };
  }, [params, script]);

  const legSymbols = useMemo(() => {
    const fromProp = (Array.isArray(symbols) ? symbols : [])
      .map((s) => String(s || '').toUpperCase())
      .filter(Boolean);
    const y = String(mergedParams.symbolY || '').toUpperCase();
    const x = String(mergedParams.symbolX || '').toUpperCase();
    let list = fromProp;
    if (mode === 'pairs' || String(templateId).includes('pairs')) {
      const pair = [y || fromProp[0] || String(symbol || '').toUpperCase(), x || fromProp[1]]
        .filter(Boolean);
      if (pair.length >= 2) list = pair;
    }
    if (!list.length && symbol) list = [String(symbol).toUpperCase()];
    return [...new Set(list)].slice(0, 3);
  }, [symbols, symbol, mergedParams.symbolY, mergedParams.symbolX, mode, templateId]);

  const multiLeg = legSymbols.length > 1;
  const primarySymbol = legSymbols[0] || String(symbol || '').toUpperCase();
  const bars = barsBySymbol[primarySymbol] || [];

  const fastPeriod = Number(mergedParams.fastPeriod) || 20;
  const slowPeriod = Number(mergedParams.slowPeriod) || 50;
  const rsiPeriod = Number(mergedParams.period) || 14;
  const oversold = Number(mergedParams.oversold) || 30;
  const overbought = Number(mergedParams.overbought) || 70;
  const coverBelow = Number(mergedParams.coverBelow) || 55;
  const lookback = Number(mergedParams.lookback) || 20;
  const touchBandPct = Number(mergedParams.touchBandPct) || 0.15;
  const orMinutes = Number(mergedParams.orMinutes) || 15;

  const chartParams = {
    fastPeriod,
    slowPeriod,
    rsiPeriod,
    oversold,
    overbought,
    coverBelow,
    lookback,
    orMinutes,
  };

  const defaultTimeframe =
    mode === 'vwap' || mode === 'or_fakeout'
      ? mode === 'or_fakeout'
        ? '1Min'
        : '5Min'
      : mode === 'bear_momentum'
        ? '15Min'
        : '1Day';
  const timeframe = inBacktest && backtestRange.timeframe ? backtestRange.timeframe : defaultTimeframe;
  const lookbackDays =
    mode === 'vwap' || mode === 'or_fakeout' ? 5 : mode === 'bear_momentum' ? 10 : 180;
  const barLimit =
    inBacktest
      ? 2000
      : mode === 'vwap' || mode === 'or_fakeout' || mode === 'bear_momentum'
        ? 500
        : 160;

  const overlayEvents = useMemo(() => {
    if (inBacktest && (backtestEvents?.length || 0) > 0) return backtestEvents;
    if (inBacktest) return [];
    return liveEvents;
  }, [inBacktest, backtestEvents, liveEvents]);

  useEffect(() => {
    if (!legSymbols.length) return undefined;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError('');
        let startIso;
        let endIso;
        if (inBacktest) {
          startIso = new Date(backtestRange.start).toISOString();
          endIso = new Date(backtestRange.end).toISOString();
        } else {
          const end = new Date(Date.now() - 60 * 60 * 1000);
          const start = new Date(end);
          start.setDate(start.getDate() - lookbackDays);
          startIso = start.toISOString();
          endIso = end.toISOString();
        }
        const entries = await Promise.all(
          legSymbols.map(async (sym) => {
            try {
              const response = await marketDataApi.getBars(sym, {
                timeframe,
                start: startIso,
                end: endIso,
                limit: barLimit,
              });
              return [sym, mapApiBars(response.data)];
            } catch (err) {
              console.warn(`AlgoSignalChart bars failed for ${sym}:`, err.message || err);
              return [sym, []];
            }
          })
        );
        if (cancelled) return;
        const next = Object.fromEntries(entries);
        setBarsBySymbol(next);
        const empty = entries.every(([, b]) => !b.length);
        if (empty) setError('Failed to load bars for chart symbols');
        else setError('');
      } catch (err) {
        if (!cancelled) {
          setBarsBySymbol({});
          setError(err.response?.data?.error || err.message || 'Failed to load bars');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    legSymbols.join(','),
    tick,
    timeframe,
    lookbackDays,
    barLimit,
    inBacktest,
    backtestRange?.start,
    backtestRange?.end,
  ]);

  const { chartData, histEvents, rsiData } = useMemo(() => {
    if (!bars.length) {
      return { chartData: [], histEvents: [], rsiData: [] };
    }

    const closes = bars.map((b) => b.close);
    const highs = bars.map((b) => b.high ?? b.close);
    const lows = bars.map((b) => b.low ?? b.close);
    const fast = smaSeries(closes, fastPeriod);
    const slow = smaSeries(closes, slowPeriod);
    const rsi = rsiSeries(closes, rsiPeriod);
    const vwap = mode === 'vwap' ? vwapSeries(bars) : null;

    // Opening range levels for the most recent session (ET)
    let sessionOrHigh = null;
    let sessionOrLow = null;
    if (mode === 'or_fakeout') {
      const openMins = 9 * 60 + 30;
      const orEndMins = openMins + Math.max(5, orMinutes);
      const etMins = (ts) => {
        const parts = Object.fromEntries(
          new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/New_York',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
            .formatToParts(new Date(ts))
            .filter((p) => p.type !== 'literal')
            .map((p) => [p.type, p.value])
        );
        let hour = Number(parts.hour);
        if (hour === 24) hour = 0;
        return {
          ymd: `${parts.year}-${parts.month}-${parts.day}`,
          mins: hour * 60 + Number(parts.minute),
        };
      };
      const lastDay = bars.length ? etMins(bars[bars.length - 1].t).ymd : null;
      const orBars = bars.filter((b) => {
        const { ymd, mins } = etMins(b.t);
        return ymd === lastDay && mins >= openMins && mins < orEndMins;
      });
      if (orBars.length) {
        sessionOrHigh = Math.max(...orBars.map((b) => b.high ?? b.close));
        sessionOrLow = Math.min(...orBars.map((b) => b.low ?? b.close));
      }
    }

    const hist = [];
    let trapDirection = null;
    const rows = bars.map((b, i) => {
      const row = {
        t: b.t,
        label: formatDay(b.t),
        close: b.close,
        fast: fast[i],
        slow: slow[i],
        rsi: rsi[i],
        vwap: vwap ? vwap[i] : null,
        orHigh: mode === 'or_fakeout' ? sessionOrHigh : null,
        orLow: mode === 'or_fakeout' ? sessionOrLow : null,
        buyMark: null,
        sellMark: null,
        breakHigh: null,
        breakLow: null,
      };

      // Long SMA crossover
      if (
        (mode === 'sma' || mode === 'sma_short') &&
        i > 0 &&
        fast[i] != null &&
        slow[i] != null &&
        fast[i - 1] != null &&
        slow[i - 1] != null
      ) {
        const golden = fast[i - 1] <= slow[i - 1] && fast[i] > slow[i];
        const death = fast[i - 1] >= slow[i - 1] && fast[i] < slow[i];
        if (mode === 'sma') {
          if (golden) {
            row.buyMark = b.close;
            hist.push({
              kind: 'golden_cross',
              label: 'Golden cross (hist)',
              t: b.t,
              price: b.close,
              side: 'buy',
            });
          }
          if (death) {
            row.sellMark = b.close;
            hist.push({
              kind: 'death_cross',
              label: 'Death cross exit (hist)',
              t: b.t,
              price: b.close,
              side: 'sell',
            });
          }
        } else {
          // Short: death = entry, golden = cover
          if (death) {
            row.sellMark = b.close;
            hist.push({
              kind: 'death_cross',
              label: 'Death cross short (hist)',
              t: b.t,
              price: b.close,
              side: 'sell',
            });
          }
          if (golden) {
            row.buyMark = b.close;
            hist.push({
              kind: 'golden_cross',
              label: 'Golden cover (hist)',
              t: b.t,
              price: b.close,
              side: 'buy',
            });
          }
        }
      }

      if (mode === 'rsi' && rsi[i] != null) {
        if (rsi[i] < oversold && (i === 0 || rsi[i - 1] == null || rsi[i - 1] >= oversold)) {
          row.buyMark = b.close;
          hist.push({
            kind: 'oversold',
            label: `RSI oversold ${rsi[i].toFixed(1)} (hist)`,
            t: b.t,
            price: b.close,
            side: 'buy',
          });
        }
        if (rsi[i] > overbought && (i === 0 || rsi[i - 1] == null || rsi[i - 1] <= overbought)) {
          row.sellMark = b.close;
          hist.push({
            kind: 'overbought',
            label: `RSI overbought ${rsi[i].toFixed(1)} (hist)`,
            t: b.t,
            price: b.close,
            side: 'sell',
          });
        }
      }

      if (mode === 'rsi_short' && rsi[i] != null) {
        if (rsi[i] > overbought && (i === 0 || rsi[i - 1] == null || rsi[i - 1] <= overbought)) {
          row.sellMark = b.close;
          hist.push({
            kind: 'overbought',
            label: `RSI short entry ${rsi[i].toFixed(1)} (hist)`,
            t: b.t,
            price: b.close,
            side: 'sell',
          });
        }
        if (rsi[i] < coverBelow && (i === 0 || rsi[i - 1] == null || rsi[i - 1] >= coverBelow)) {
          row.buyMark = b.close;
          hist.push({
            kind: 'cover',
            label: `RSI cover ${rsi[i].toFixed(1)} (hist)`,
            t: b.t,
            price: b.close,
            side: 'buy',
          });
        }
      }

      if (mode === 'breakout' && i >= lookback) {
        const priorHigh = Math.max(...highs.slice(i - lookback, i));
        row.breakHigh = priorHigh;
        const prevPrior =
          i > lookback ? Math.max(...highs.slice(i - 1 - lookback, i - 1)) : priorHigh;
        if (b.close > priorHigh && !(bars[i - 1].close > prevPrior)) {
          row.buyMark = b.close;
          hist.push({
            kind: 'breakout',
            label: 'Breakout (hist)',
            t: b.t,
            price: b.close,
            side: 'buy',
          });
        }
      }

      if (mode === 'breakdown' && i >= lookback) {
        const priorLow = Math.min(...lows.slice(i - lookback, i));
        row.breakLow = priorLow;
        const prevPrior =
          i > lookback ? Math.min(...lows.slice(i - 1 - lookback, i - 1)) : priorLow;
        if (b.close < priorLow && !(bars[i - 1].close < prevPrior)) {
          row.sellMark = b.close;
          hist.push({
            kind: 'breakdown',
            label: 'Breakdown short (hist)',
            t: b.t,
            price: b.close,
            side: 'sell',
          });
        }
      }

      // Lightweight VWAP touch markers (not full state machine — visual guide)
      if (mode === 'vwap' && vwap && vwap[i] != null && i > 0) {
        const band = vwap[i] * (touchBandPct / 100);
        const touched = b.low <= vwap[i] + band && b.high >= vwap[i] - band;
        const bounceLong = touched && b.close > vwap[i] && bars[i - 1].close <= vwap[i - 1];
        const rejectShort = touched && b.close < vwap[i] && bars[i - 1].close >= vwap[i - 1];
        if (templateId === 'vwap_rejection_short') {
          if (rejectShort) {
            row.sellMark = b.close;
            hist.push({
              kind: 'vwap_short',
              label: 'VWAP rejection (hist)',
              t: b.t,
              price: b.close,
              side: 'sell',
            });
          }
        } else if (bounceLong) {
          row.buyMark = b.close;
          hist.push({
            kind: 'vwap_entry',
            label: 'VWAP pullback bounce (hist)',
            t: b.t,
            price: b.close,
            side: 'buy',
          });
        }
      }

      // Opening range fakeout / liquidity grab (latest session OR levels)
      if (
        mode === 'or_fakeout' &&
        sessionOrHigh != null &&
        sessionOrLow != null &&
        Number.isFinite(sessionOrHigh) &&
        Number.isFinite(sessionOrLow)
      ) {
        const openMins = 9 * 60 + 30;
        const orEndMins = openMins + Math.max(5, orMinutes);
        const parts = Object.fromEntries(
          new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/New_York',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
            .formatToParts(new Date(b.t))
            .filter((p) => p.type !== 'literal')
            .map((p) => [p.type, p.value])
        );
        let hour = Number(parts.hour);
        if (hour === 24) hour = 0;
        const mins = hour * 60 + Number(parts.minute);
        const lastDay = bars.length
          ? (() => {
              const lp = Object.fromEntries(
                new Intl.DateTimeFormat('en-US', {
                  timeZone: 'America/New_York',
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                })
                  .formatToParts(new Date(bars[bars.length - 1].t))
                  .filter((p) => p.type !== 'literal')
                  .map((p) => [p.type, p.value])
              );
              return `${lp.year}-${lp.month}-${lp.day}`;
            })()
          : null;
        const ymd = `${parts.year}-${parts.month}-${parts.day}`;
        if (ymd === lastDay && mins >= orEndMins) {
          if (!trapDirection) {
            if ((b.high ?? b.close) > sessionOrHigh) trapDirection = 'UP';
            else if ((b.low ?? b.close) < sessionOrLow) trapDirection = 'DOWN';
          }
          if (trapDirection === 'DOWN' && b.close > sessionOrLow) {
            row.buyMark = b.close;
            hist.push({
              kind: 'or_fakeout_long',
              label: 'OR fakeout long (hist)',
              t: b.t,
              price: b.close,
              side: 'buy',
            });
            trapDirection = 'DONE';
          } else if (trapDirection === 'UP' && b.close < sessionOrHigh) {
            row.sellMark = b.close;
            hist.push({
              kind: 'or_fakeout_short',
              label: 'OR fakeout short (hist)',
              t: b.t,
              price: b.close,
              side: 'sell',
            });
            trapDirection = 'DONE';
          }
        }
      }

      return row;
    });

    if (inBacktest) {
      for (const row of rows) {
        row.buyMark = null;
        row.sellMark = null;
      }
      hist.length = 0;
    }

    for (const ev of overlayEvents) {
      if (!ev?.timestamp && !ev?.t) continue;
      const evSym = String(ev.symbol || '').toUpperCase();
      // On multi-leg charts the primary pane only plots this symbol's markers
      if (multiLeg && evSym && evSym !== primarySymbol) continue;
      const evT = new Date(ev.timestamp || ev.t).getTime();
      if (!Number.isFinite(evT)) continue;
      let best = -1;
      let bestDiff = Infinity;
      for (let i = 0; i < rows.length; i++) {
        const diff = Math.abs(new Date(rows[i].t).getTime() - evT);
        if (diff < bestDiff) {
          bestDiff = diff;
          best = i;
        }
      }
      if (best < 0) continue;
      // Match within ~2 bar-widths for daily, or 2 days for intraday noise
      const maxDiffMs = timeframe.includes('Day') ? 36 * 60 * 60 * 1000 : 6 * 60 * 60 * 1000;
      if (bestDiff > maxDiffMs) continue;
      if (eventIsBuy(ev)) rows[best].buyMark = ev.price != null ? Number(ev.price) : rows[best].close;
      if (eventIsSell(ev)) rows[best].sellMark = ev.price != null ? Number(ev.price) : rows[best].close;
    }

    return {
      chartData: rows,
      histEvents: hist.slice(-24).reverse(),
      rsiData: rows,
    };
  }, [
    bars,
    mode,
    templateId,
    fastPeriod,
    slowPeriod,
    rsiPeriod,
    oversold,
    overbought,
    coverBelow,
    lookback,
    touchBandPct,
    orMinutes,
    overlayEvents,
    inBacktest,
    timeframe,
    multiLeg,
    primarySymbol,
  ]);

  const timeline = useMemo(() => {
    if (inBacktest) {
      return [...overlayEvents]
        .map((e) => ({
          ...e,
          t: e.timestamp || e.t,
          source: 'backtest',
        }))
        .reverse()
        .slice(0, 80);
    }
    const live = [...liveEvents]
      .map((e) => ({
        ...e,
        t: e.timestamp || e.t,
        source: 'live',
      }))
      .reverse();
    const hist = histEvents.map((e) => ({ ...e, source: 'hist' }));
    return [...live, ...hist].slice(0, 24);
  }, [inBacktest, overlayEvents, liveEvents, histEvents]);

  const legPanes = useMemo(() => {
    if (!multiLeg) return [];
    const paneH = Math.max(160, Math.floor(height * 0.7));
    return legSymbols.map((sym) => ({
      symbol: sym,
      height: paneH,
      data: buildLegChartData(barsBySymbol[sym] || [], overlayEvents, sym, timeframe),
    }));
  }, [multiLeg, legSymbols, barsBySymbol, overlayEvents, timeframe, height]);

  if (!legSymbols.length) {
    return (
      <div className="bg-white rounded-lg shadow-md border border-gray-100 p-4 text-xs text-gray-500">
        Set a symbol in the script (or load a template) to see the signal chart.
      </div>
    );
  }

  const showSma = !multiLeg && (mode === 'sma' || mode === 'sma_short' || mode === 'bear_momentum');
  const showRsiPane = !multiLeg && (mode === 'rsi' || mode === 'rsi_short');

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-bold text-gray-800 flex items-center gap-1.5 flex-wrap">
          <FontAwesomeIcon icon={faChartLine} className="text-indigo-600 text-xs" />
          Signal chart · {legSymbols.join(' · ')}
          {templateName ? (
            <span className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">
              {templateName}
            </span>
          ) : null}
          <span className="text-[10px] font-normal text-gray-500 ml-1">
            {modeLabel(mode, chartParams)} · {timeframe}
            {inBacktest ? ' · backtest window' : ''}
            {multiLeg ? ' · multi-leg' : ''}
          </span>
        </h3>
        <button
          type="button"
          onClick={() => setTick((n) => n + 1)}
          className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
          title="Refresh bars"
        >
          <FontAwesomeIcon icon={faSync} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {inBacktest && (
        <div className="px-3 py-1.5 text-[11px] bg-teal-50 border-b border-teal-100 text-teal-900">
          Showing bars for the backtest range
          {backtestEvents?.length
            ? ` · ${backtestEvents.length} emitted signal${backtestEvents.length === 1 ? '' : 's'}`
            : ' · no helpers.emitSignal() calls (fills still listed under Trades)'}
        </div>
      )}

      <div className="p-3">
        {loading && !(multiLeg ? legPanes.some((p) => p.data.length) : chartData.length) ? (
          <div className="h-[200px] flex items-center justify-center text-gray-500 text-xs">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
            Loading bars…
          </div>
        ) : error ? (
          <div className="text-xs text-red-600 py-6 text-center">{error}</div>
        ) : multiLeg ? (
          <>
            <div className="space-y-3">
              {legPanes.map((pane) => (
                <div key={pane.symbol}>
                  <div className="text-[10px] font-semibold uppercase text-gray-500 mb-1">
                    {pane.symbol}
                    {!pane.data.length ? ' · no bars' : ''}
                  </div>
                  <div style={{ width: '100%', height: pane.height }}>
                    <ResponsiveContainer>
                      <ComposedChart data={pane.data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={24} />
                        <YAxis
                          yAxisId="price"
                          domain={['auto', 'auto']}
                          tick={{ fontSize: 10 }}
                          width={52}
                          tickFormatter={(v) => (v >= 100 ? v.toFixed(0) : v.toFixed(2))}
                        />
                        <Tooltip
                          contentStyle={{ fontSize: 11 }}
                          labelFormatter={(_, payload) => {
                            const t = payload?.[0]?.payload?.t;
                            return t ? formatTime(t) : '';
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line
                          yAxisId="price"
                          type="monotone"
                          dataKey="close"
                          name={`${pane.symbol} close`}
                          stroke="#111827"
                          dot={false}
                          strokeWidth={1.5}
                          isAnimationActive={false}
                        />
                        <Scatter
                          yAxisId="price"
                          dataKey="buyMark"
                          name="Buy / cover"
                          fill="#16a34a"
                          shape="triangle"
                          isAnimationActive={false}
                        />
                        <Scatter
                          yAxisId="price"
                          dataKey="sellMark"
                          name="Sell / short"
                          fill="#dc2626"
                          shape="triangleDown"
                          isAnimationActive={false}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] font-semibold uppercase text-gray-500 mb-1">
                  {inBacktest ? 'Backtest signals (all legs)' : 'When things happen'}
                </div>
                <div className="max-h-36 overflow-y-auto border border-gray-100 rounded bg-gray-50 text-xs">
                  {timeline.length === 0 ? (
                    <div className="p-3 text-gray-500">
                      No markers yet for these symbols.
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {timeline.map((ev, i) => (
                        <li key={`${ev.t}-${ev.kind}-${i}`} className="px-2 py-1.5 flex items-start gap-2">
                          <span
                            className="mt-1 w-2 h-2 rounded-full shrink-0"
                            style={{ background: EVENT_COLORS[ev.kind] || '#6366f1' }}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-800 truncate">
                              {ev.symbol ? `${ev.symbol} · ` : ''}
                              {ev.label || ev.kind}
                              {ev.source === 'backtest' && (
                                <span className="ml-1 text-[10px] text-teal-700 font-semibold">BT</span>
                              )}
                            </div>
                            <div className="text-[10px] text-gray-500">
                              {formatTime(ev.t)}
                              {ev.price != null && Number.isFinite(Number(ev.price))
                                ? ` · $${Number(ev.price).toFixed(2)}`
                                : ''}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div className="text-[11px] text-gray-600 space-y-1 pt-1">
                <p>Each pane is one leg from the IDE Symbols field (or pair template).</p>
                <p>
                  <span className="inline-block w-2 h-2 rounded-full bg-green-600 mr-1" />
                  Green = buy / cover ·{' '}
                  <span className="inline-block w-2 h-2 rounded-full bg-red-600 mr-1" />
                  Red = sell / short
                </p>
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={{ width: '100%', height }}>
              <ResponsiveContainer>
                <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={24} />
                  <YAxis
                    yAxisId="price"
                    domain={['auto', 'auto']}
                    tick={{ fontSize: 10 }}
                    width={52}
                    tickFormatter={(v) => (v >= 100 ? v.toFixed(0) : v.toFixed(2))}
                  />
                  <Tooltip
                    contentStyle={{ fontSize: 11 }}
                    labelFormatter={(_, payload) => {
                      const t = payload?.[0]?.payload?.t;
                      return t ? formatTime(t) : '';
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line
                    yAxisId="price"
                    type="monotone"
                    dataKey="close"
                    name={`${primarySymbol} close`}
                    stroke="#111827"
                    dot={false}
                    strokeWidth={1.5}
                    isAnimationActive={false}
                  />
                  {showSma && (
                    <>
                      <Line
                        yAxisId="price"
                        type="monotone"
                        dataKey="fast"
                        name={`SMA ${fastPeriod}`}
                        stroke="#2563eb"
                        dot={false}
                        strokeWidth={1.25}
                        isAnimationActive={false}
                      />
                      <Line
                        yAxisId="price"
                        type="monotone"
                        dataKey="slow"
                        name={`SMA ${slowPeriod}`}
                        stroke="#f59e0b"
                        dot={false}
                        strokeWidth={1.25}
                        isAnimationActive={false}
                      />
                    </>
                  )}
                  {mode === 'breakout' && (
                    <Line
                      yAxisId="price"
                      type="stepAfter"
                      dataKey="breakHigh"
                      name={`${lookback}d high`}
                      stroke="#94a3b8"
                      dot={false}
                      strokeDasharray="4 3"
                      strokeWidth={1}
                      isAnimationActive={false}
                    />
                  )}
                  {mode === 'breakdown' && (
                    <Line
                      yAxisId="price"
                      type="stepAfter"
                      dataKey="breakLow"
                      name={`${lookback}d low`}
                      stroke="#94a3b8"
                      dot={false}
                      strokeDasharray="4 3"
                      strokeWidth={1}
                      isAnimationActive={false}
                    />
                  )}
                  {mode === 'vwap' && (
                    <Line
                      yAxisId="price"
                      type="monotone"
                      dataKey="vwap"
                      name="Session VWAP"
                      stroke="#0891b2"
                      dot={false}
                      strokeWidth={1.5}
                      isAnimationActive={false}
                    />
                  )}
                  {mode === 'or_fakeout' && (
                    <>
                      <Line
                        yAxisId="price"
                        type="monotone"
                        dataKey="orHigh"
                        name="OR High"
                        stroke="#f59e0b"
                        dot={false}
                        strokeDasharray="4 3"
                        strokeWidth={1.25}
                        isAnimationActive={false}
                      />
                      <Line
                        yAxisId="price"
                        type="monotone"
                        dataKey="orLow"
                        name="OR Low"
                        stroke="#6366f1"
                        dot={false}
                        strokeDasharray="4 3"
                        strokeWidth={1.25}
                        isAnimationActive={false}
                      />
                    </>
                  )}
                  <Scatter
                    yAxisId="price"
                    dataKey="buyMark"
                    name={
                      mode === 'sma_short' ||
                      mode === 'rsi_short' ||
                      mode === 'breakdown' ||
                      mode === 'or_fakeout' ||
                      templateId === 'vwap_rejection_short'
                        ? 'Long / cover'
                        : 'Buy / long'
                    }
                    fill="#16a34a"
                    shape="triangle"
                    isAnimationActive={false}
                  />
                  <Scatter
                    yAxisId="price"
                    dataKey="sellMark"
                    name={
                      mode === 'sma_short' ||
                      mode === 'rsi_short' ||
                      mode === 'breakdown' ||
                      mode === 'or_fakeout' ||
                      templateId === 'vwap_rejection_short'
                        ? 'Short / sell'
                        : 'Sell / exit'
                    }
                    fill="#dc2626"
                    shape="triangleDown"
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {showRsiPane && (
              <div style={{ width: '100%', height: 120 }} className="mt-2">
                <ResponsiveContainer>
                  <LineChart data={rsiData} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={24} hide />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} width={52} />
                    {mode === 'rsi' && (
                      <ReferenceLine y={oversold} stroke="#16a34a" strokeDasharray="3 3" />
                    )}
                    <ReferenceLine y={overbought} stroke="#dc2626" strokeDasharray="3 3" />
                    {mode === 'rsi_short' && (
                      <ReferenceLine y={coverBelow} stroke="#16a34a" strokeDasharray="3 3" />
                    )}
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                    <Line
                      type="monotone"
                      dataKey="rsi"
                      name={`RSI(${rsiPeriod})`}
                      stroke="#7c3aed"
                      dot={false}
                      strokeWidth={1.5}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] font-semibold uppercase text-gray-500 mb-1">
                  {inBacktest ? 'Backtest signals' : 'When things happen (live + recent hist)'}
                </div>
                <div className="max-h-36 overflow-y-auto border border-gray-100 rounded bg-gray-50 text-xs">
                  {timeline.length === 0 ? (
                    <div className="p-3 text-gray-500">
                      {inBacktest
                        ? 'No helpers.emitSignal() calls in this run. Add emitSignal in your script to mark entries/exits here.'
                        : 'No markers yet. Historical signals follow the selected template after bars load; live buys/sells appear when the algo fires.'}
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {timeline.map((ev, i) => (
                        <li key={`${ev.t}-${ev.kind}-${i}`} className="px-2 py-1.5 flex items-start gap-2">
                          <span
                            className="mt-1 w-2 h-2 rounded-full shrink-0"
                            style={{ background: EVENT_COLORS[ev.kind] || '#6366f1' }}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-800 truncate">
                              {ev.label || ev.kind}
                              {ev.source === 'live' && (
                                <span className="ml-1 text-[10px] text-indigo-600 font-semibold">LIVE</span>
                              )}
                              {ev.source === 'backtest' && (
                                <span className="ml-1 text-[10px] text-teal-700 font-semibold">BT</span>
                              )}
                            </div>
                            <div className="text-[10px] text-gray-500">
                              {formatTime(ev.t)}
                              {ev.price != null && Number.isFinite(Number(ev.price))
                                ? ` · $${Number(ev.price).toFixed(2)}`
                                : ''}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div className="text-[11px] text-gray-600 space-y-1 pt-1">
                <p>
                  <span className="inline-block w-2 h-2 rounded-full bg-green-600 mr-1" />
                  Green = buy / cover
                </p>
                <p>
                  <span className="inline-block w-2 h-2 rounded-full bg-red-600 mr-1" />
                  Red = sell / short / exit
                </p>
                <p className="text-gray-500">
                  {inBacktest ? (
                    <>
                      Markers come from <code className="mx-1 bg-gray-100 px-1 rounded">helpers.emitSignal</code> during
                      the backtest (same call as live). Falls back to trade fills if none were emitted.
                    </>
                  ) : (
                    <>
                      Chart overlays and historical markers switch with the template dropdown (and follow{' '}
                      <code className="bg-gray-100 px-1 rounded">const</code> periods in the editor). Live
                      markers come from <code className="mx-1 bg-gray-100 px-1 rounded">helpers.emitSignal</code>{' '}
                      while the runner is connected.
                    </>
                  )}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AlgoSignalChart;
